import Phaser from "phaser";
import type { SharpZoomPlan } from "./plan.ts";

/**
 * The resample pass of sharp scaling (P1.9-T5): a Phaser `Shader` quad over the whole canvas that reads the
 * buffer the world was drawn into at the whole zoom and writes each device pixel as the box average of the
 * buffer texels its span covers. The buffer is sampled with linear filtering, and two reads an axis, four in
 * all, reproduce the box weights exactly; the reads' places and weights are the closed form of `linearTaps`
 * in `plan.ts`, unit-tested against `boxWeights`, so the shader has no branch and no division.
 *
 * The shader reads each device pixel's position from `gl_FragCoord`, so the quad only has to cover the canvas.
 * The buffer, like every Phaser framebuffer, holds its top row last, which the shader undoes.
 */

/**
 * The fragment shader.
 */
export const RESAMPLE_FRAGMENT_SHADER = [
  "#pragma phaserTemplate(shaderName)",
  "precision highp float;",
  "uniform sampler2D uMainSampler;",
  "uniform vec2 uInputSize;",
  "uniform vec2 uOutputSize;",
  "uniform vec2 uOrigin;",
  "uniform vec3 uStep;",
  "void main ()",
  "{",
  "    vec2 pixel = vec2(gl_FragCoord.x - 0.5, uOutputSize.y - gl_FragCoord.y - 0.5);",
  "    vec2 start = uOrigin + pixel * uStep.x;",
  "    vec2 first = floor(start);",
  "    vec2 f = start - first;",
  "    vec2 near = first + 0.5 + min(vec2(1.0), f + (uStep.x - 1.0)) * uStep.y;",
  "    vec2 far = first + 2.5 - uStep.y;",
  "    vec2 weight = min(vec2(1.0), (1.0 - f) * uStep.z);",
  "    vec2 rest = 1.0 - weight;",
  "    vec2 u = vec2(near.x, far.x) / uInputSize.x;",
  "    vec2 v = 1.0 - vec2(near.y, far.y) / uInputSize.y;",
  "    gl_FragColor = weight.x * weight.y * texture2D(uMainSampler, vec2(u.x, v.x))",
  "        + rest.x * weight.y * texture2D(uMainSampler, vec2(u.y, v.x))",
  "        + weight.x * rest.y * texture2D(uMainSampler, vec2(u.x, v.y))",
  "        + rest.x * rest.y * texture2D(uMainSampler, vec2(u.y, v.y));",
  "}",
].join("\n");

/**
 * What the shader is given each frame, written in place so a frame allocates nothing.
 */
export interface ResampleUniforms {
  /** The buffer's size in texels. */
  readonly inputSize: [number, number];
  /** The canvas's size in device pixels. */
  readonly outputSize: [number, number];
  /** The buffer coordinate of the device grid's top left edge. */
  readonly origin: [number, number];
  /** Buffer texels per device pixel, its reciprocal, and the reciprocal of one less than it. */
  readonly step: [number, number, number];
}

/**
 * Uniforms before the first frame.
 *
 * @returns The uniforms.
 */
export function createResampleUniforms(): ResampleUniforms {
  return { inputSize: [1, 1], outputSize: [1, 1], origin: [0, 0], step: [1, 1, 1] };
}

/**
 * Writes a frame's plan and the canvas's size into the uniforms.
 *
 * @param uniforms The uniforms.
 * @param plan The frame's plan, active, so its step is above 1 and below 2.
 * @param bufferWidth The buffer's width as allocated, at least the plan's.
 * @param bufferHeight The buffer's height as allocated, at least the plan's.
 * @param width The canvas's width in device pixels.
 * @param height The canvas's height in device pixels.
 */
export function writeResampleUniforms(uniforms: ResampleUniforms, plan: Readonly<SharpZoomPlan>, bufferWidth: number, bufferHeight: number, width: number, height: number): void {
  uniforms.inputSize[0] = bufferWidth;
  uniforms.inputSize[1] = bufferHeight;
  uniforms.outputSize[0] = width;
  uniforms.outputSize[1] = height;
  uniforms.origin[0] = plan.originX;
  uniforms.origin[1] = plan.originY;
  uniforms.step[0] = plan.step;
  uniforms.step[1] = 1 / plan.step;
  uniforms.step[2] = 1 / (plan.step - 1);
}

/**
 * Makes the resample quad, reading a texture with the uniforms it is given each time it draws.
 *
 * @param scene The scene.
 * @param buffer The texture the world is drawn into.
 * @param uniforms The uniforms, written each frame.
 * @returns The quad, at the origin with its origin at the top left and no size yet, in no display list.
 */
export function createResampleQuad(scene: Phaser.Scene, buffer: Phaser.Textures.Texture, uniforms: ResampleUniforms): Phaser.GameObjects.Shader {
  const config: Phaser.Types.GameObjects.Shader.ShaderQuadConfig = {
    name: "SharpResample",
    fragmentSource: RESAMPLE_FRAGMENT_SHADER,
    /**
     * Hands the shader the frame's settings.
     *
     * @param setUniform Sets one uniform.
     */
    setupUniforms(setUniform: (name: string, value: unknown) => void) {
      setUniform("uMainSampler", 0);
      setUniform("uInputSize", uniforms.inputSize);
      setUniform("uOutputSize", uniforms.outputSize);
      setUniform("uOrigin", uniforms.origin);
      setUniform("uStep", uniforms.step);
    },
  };
  return new Phaser.GameObjects.Shader(scene, config, 0, 0, 1, 1, [buffer]).setOrigin(0);
}
